import React, { useState } from 'react'
import { isEmpty } from 'lodash'
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Typography
} from 'antd'
import { useDeepEffect } from '../../../../utils/hooks'

const { Option } = Select
const { Text } = Typography

const TIMEZONES = [
  'Asia/Bangkok',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'UTC',
  'America/New_York',
  'Europe/London'
]

const DEFAULT_STATE = {
  mode: 'minutes',
  minutes: 15,
  hours: 1,
  time: '09:00',
  raw: '',
  weekdaysOnly: false,
  // Optional hour range in 24h. Empty string = no range (use mode default).
  hourFrom: '',
  hourTo: ''
}

// Match an hour field that's either "*", "*/N" (step), a single "N", or "A-B".
const HOUR_RANGE_RE = /^(\d{1,2})-(\d{1,2})$/
const STEP_HOUR_RE = /^\*\/(\d+)$/

export const parseCron = (cron) => {
  if (!cron) return { ...DEFAULT_STATE }
  const parts = String(cron).trim().split(/\s+/)
  if (parts.length !== 5) {
    return { ...DEFAULT_STATE, mode: 'custom', raw: cron }
  }
  const [m, h, dom, mon, dow] = parts
  if (dom !== '*' || mon !== '*') {
    return { ...DEFAULT_STATE, mode: 'custom', raw: cron }
  }
  const weekdaysOnly = dow === '1-5'
  if (!weekdaysOnly && dow !== '*') {
    // Unknown day_of_week pattern (e.g. specific days) → fall back to custom.
    return { ...DEFAULT_STATE, mode: 'custom', raw: cron }
  }

  // Parse hour part — accept "*", "*/N", or "A-B" range.
  let hourFrom = ''
  let hourTo = ''
  const hourMatch = HOUR_RANGE_RE.exec(h)
  if (hourMatch) {
    hourFrom = hourMatch[1]
    hourTo = hourMatch[2]
  }

  if (/^\*\/\d+$/.test(m) && (h === '*' || hourMatch)) {
    return {
      ...DEFAULT_STATE,
      mode: 'minutes',
      minutes: parseInt(m.slice(2), 10),
      weekdaysOnly,
      hourFrom,
      hourTo,
      raw: cron
    }
  }
  if (m === '0' && STEP_HOUR_RE.test(h)) {
    // "0 */N * * dow" — fixed step from hour 0
    return {
      ...DEFAULT_STATE,
      mode: 'hours',
      hours: parseInt(h.slice(2), 10),
      weekdaysOnly,
      raw: cron
    }
  }
  if (/^\d+$/.test(m) && /^\d+$/.test(h)) {
    const hh = String(h).padStart(2, '0')
    const mm = String(m).padStart(2, '0')
    return {
      ...DEFAULT_STATE,
      mode: 'daily',
      time: `${hh}:${mm}`,
      weekdaysOnly,
      raw: cron
    }
  }
  return { ...DEFAULT_STATE, mode: 'custom', raw: cron }
}

const _hourField = (s) => {
  const from = s.hourFrom === '' || s.hourFrom == null ? null : Number(s.hourFrom)
  const to = s.hourTo === '' || s.hourTo == null ? null : Number(s.hourTo)
  if (from != null && to != null && from <= to && from >= 0 && to <= 23) {
    return `${from}-${to}`
  }
  return '*'
}

const _dowField = (s) => (s.weekdaysOnly ? '1-5' : '*')

export const buildCron = (s) => {
  switch (s.mode) {
    case 'minutes':
      return `*/${s.minutes || 1} ${_hourField(s)} * * ${_dowField(s)}`
    case 'hours':
      // Hour-step mode doesn't combine cleanly with an hour range; ignore range here.
      return `0 */${s.hours || 1} * * ${_dowField(s)}`
    case 'daily': {
      const [hh, mm] = (s.time || '09:00').split(':')
      return `${parseInt(mm, 10)} ${parseInt(hh, 10)} * * ${_dowField(s)}`
    }
    case 'custom':
      return s.raw || ''
    default:
      return s.raw || ''
  }
}

const _suffixThai = (s) => {
  const parts = []
  if (s.hourFrom !== '' && s.hourTo !== '' && (s.mode === 'minutes')) {
    parts.push(`${s.hourFrom}-${s.hourTo} น.`)
  }
  if (s.weekdaysOnly) parts.push('จ-ศ')
  return parts.length ? ` (${parts.join(', ')})` : ''
}

export const describeCron = (cron) => {
  const s = parseCron(cron)
  switch (s.mode) {
    case 'minutes':
      return `ทุก ${s.minutes} นาที${_suffixThai(s)}`
    case 'hours':
      return `ทุก ${s.hours} ชั่วโมง${_suffixThai(s)}`
    case 'daily':
      return `รายวัน เวลา ${s.time}${_suffixThai(s)}`
    case 'custom':
      return cron || '-'
    default:
      return '-'
  }
}

const ScheduleForm = (props) => {
  const { visible, onOk, onCancel, formData, form } = props
  const { getFieldDecorator, validateFields, setFieldsValue } = form
  const [state, setState] = useState(DEFAULT_STATE)

  useDeepEffect(() => {
    if (!isEmpty(formData)) {
      setState(parseCron(formData.cron_expression))
      setFieldsValue({
        timezone: formData.timezone || 'Asia/Bangkok'
      })
    } else {
      setState(DEFAULT_STATE)
    }
    return () => {
      form.resetFields()
    }
  }, [formData, visible])

  const update = (patch) => setState((s) => ({ ...s, ...patch }))

  const onSubmit = (e) => {
    e.preventDefault()
    validateFields(async (err, values) => {
      if (!err) {
        const data = {
          cron_expression: buildCron(state),
          timezone: values.timezone || 'Asia/Bangkok'
        }
        onOk(data)
      }
    })
  }

  const cronPreview = buildCron(state)

  return (
    <Modal
      title={`ตั้งเวลาดึงราคา: ${formData.name || ''}`}
      visible={visible}
      footer={null}
      onCancel={onCancel}
      width={640}
      destroyOnClose>
      <Form onSubmit={onSubmit} layout="vertical">
        <Form.Item label="ความถี่">
          <Radio.Group
            value={state.mode}
            onChange={(e) => update({ mode: e.target.value })}>
            <Radio.Button value="minutes">ทุก X นาที</Radio.Button>
            <Radio.Button value="hours">ทุก X ชั่วโมง</Radio.Button>
            <Radio.Button value="daily">รายวัน</Radio.Button>
            <Radio.Button value="custom">กำหนดเอง</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {state.mode === 'minutes' && (
          <Form.Item label="ทุกกี่นาที (1-59)">
            <InputNumber
              min={1}
              max={59}
              value={state.minutes}
              onChange={(v) => update({ minutes: v || 1 })}
            />
          </Form.Item>
        )}
        {state.mode === 'hours' && (
          <Form.Item label="ทุกกี่ชั่วโมง (1-23)">
            <InputNumber
              min={1}
              max={23}
              value={state.hours}
              onChange={(v) => update({ hours: v || 1 })}
            />
          </Form.Item>
        )}
        {state.mode === 'daily' && (
          <Form.Item label="เวลา">
            <Input
              type="time"
              value={state.time}
              onChange={(e) => update({ time: e.target.value })}
              style={{ width: 160 }}
            />
          </Form.Item>
        )}
        {state.mode === 'custom' && (
          <Form.Item
            label="Cron Expression"
            extra="รูปแบบ: min hour dayOfMonth month dayOfWeek (เช่น */15 * * * *)">
            <Input
              value={state.raw}
              onChange={(e) => update({ raw: e.target.value })}
              placeholder="*/15 * * * *"
            />
          </Form.Item>
        )}

        {state.mode !== 'custom' && (
          <Form.Item label="ตลาดปิด">
            <Checkbox
              checked={state.weekdaysOnly}
              onChange={(e) => update({ weekdaysOnly: e.target.checked })}>
              ข้ามเสาร์-อาทิตย์ (รันเฉพาะ จ-ศ)
            </Checkbox>
          </Form.Item>
        )}

        {state.mode === 'minutes' && (
          <Form.Item
            label="ช่วงเวลา (ชั่วโมง)"
            extra="ปล่อยว่าง = ทั้งวัน. ใช้สำหรับจำกัดช่วงตลาดเปิด เช่น 9-17">
            <Row gutter={8} type="flex" align="middle">
              <Col>
                <InputNumber
                  min={0}
                  max={23}
                  placeholder="เริ่ม"
                  value={state.hourFrom === '' ? undefined : state.hourFrom}
                  onChange={(v) =>
                    update({ hourFrom: v == null ? '' : String(v) })
                  }
                  style={{ width: 80 }}
                />
              </Col>
              <Col>ถึง</Col>
              <Col>
                <InputNumber
                  min={0}
                  max={23}
                  placeholder="สิ้นสุด"
                  value={state.hourTo === '' ? undefined : state.hourTo}
                  onChange={(v) =>
                    update({ hourTo: v == null ? '' : String(v) })
                  }
                  style={{ width: 80 }}
                />
              </Col>
              <Col>น.</Col>
            </Row>
          </Form.Item>
        )}

        <Form.Item label="สรุป">
          <div>
            <Text strong>{describeCron(cronPreview)}</Text>
          </div>
          <Text
            type="secondary"
            style={{ fontFamily: 'monospace', fontSize: 12 }}>
            {cronPreview || '—'}
          </Text>
        </Form.Item>

        <Form.Item label="Timezone">
          {getFieldDecorator('timezone', {
            initialValue: 'Asia/Bangkok'
          })(
            <Select>
              {TIMEZONES.map((tz) => (
                <Option key={tz} value={tz}>
                  {tz}
                </Option>
              ))}
            </Select>
          )}
        </Form.Item>

        <Row type="flex" gutter={8} justify="end">
          <Col>
            <Button onClick={() => onCancel()}>ยกเลิก</Button>
          </Col>
          <Col>
            <Button type="primary" htmlType="submit">
              บันทึก
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}

const WrappedScheduleForm = Form.create({ name: 'ScheduleForm' })(ScheduleForm)
export default WrappedScheduleForm
